import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import {Button, Stack, Typography} from '@mui/material';
import {GeolocationControl, Map, Placemark, SearchControl, YMaps, ZoomControl} from '@pbe/react-yandex-maps';
import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {MapEvent} from 'yandex-maps';
import {useLocation} from 'react-router-dom';
import {useStore} from 'zustand';
import {useRequestDataGate} from '../../hooks/useRequestDataGate';
import {useStoresContext} from '../../hooks/useStoresContext';
import {useRequestMapStore} from '../../store/useRequestMapStore';
import {FallbackWithChildren} from '../FallbackWithChildren';
import {getBalloonContent, getPreparePoints, getRedirectButton, prepareMapWidth, renderClusterer} from './helpers';
import {STYLES} from './styles';
import {get} from 'lodash';
import {IServiceRequest} from '../../../../../../shared/types/serviceRequest.types';
import {MapLegend} from '../MapLegend';

interface Props {
  serviceType: string;
}

export const RequestMap: FC<Props> = ({serviceType}) => {
  useRequestDataGate();
  const {pathname} = useLocation();
  const {chartViewStore} = useStoresContext(serviceType);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const requests = useRequestMapStore((s) => s.requests);
  const isLoading = useRequestMapStore((s) => s.isLoading);
  const setSelectedContractId = useRequestMapStore((s) => s.setSelectedContractId);
  const [zoomLevel, setZoomLevel] = useState(3);
  const [mapWidth, setMapWidth] = useState<string>('100%');
  const isPrimaryFullWidth = useStore(chartViewStore, (s) => s.isPrimaryFullWidth());

  const preparePoints = useMemo(getPreparePoints, [requests]);
  const points = preparePoints(get(requests, 'content', []) as IServiceRequest[]);

  const handleZoomChange = (event: MapEvent) => {
    const newZoomLevel = event.get('newZoom');
    setZoomLevel(newZoomLevel);
  };

  const handleBackClick = () => {
    setSelectedContractId(null);
  };

  const goToServiceRequest = (e: MouseEvent) => {
    e.preventDefault();
    const requestNumber = (e.target as HTMLAnchorElement).id.split('-')[1];
    const url = `/serviceRequests/${requestNumber}`;
    sessionStorage.setItem('current', url);
    const newWindow = window.open(url, '_blank');
    newWindow?.addEventListener('load', () => sessionStorage.setItem('current', pathname), {once: true});
  };

  useEffect(() => {
    setMapWidth(prepareMapWidth(containerRef.current));
  }, [isPrimaryFullWidth]);

  const containerPx = useMemo(() => ({px: isPrimaryFullWidth ? '8px' : '46px'}), [isPrimaryFullWidth]);
  const showClusterer = useMemo(() => zoomLevel < 18, [zoomLevel]);
  const stackId = useMemo(() => `s-${Math.random()}-${requests.length}`, [requests]);
  const clustererId = useMemo(() => `c-${Math.random()}-${requests.length}`, [requests]);

  return (
    <Stack gap={2}>
      <Stack alignItems="flex-start">
        <Button startIcon={<KeyboardReturnIcon />} onClick={handleBackClick}>
          Назад
        </Button>
      </Stack>
      <Typography sx={STYLES.chartTitle}>Значение на карте</Typography>
      <Stack gap={1} key={stackId} sx={containerPx} ref={containerRef} minHeight={400}>
        <FallbackWithChildren isLoading={isLoading}>
          <YMaps query={{apikey: process.env.REACT_APP_YANDEX_MAPS_API_KEY, lang: 'ru_RU'}}>
            <Map
              height={400}
              defaultState={{center: [60, 100], zoom: zoomLevel}}
              width={mapWidth}
              onBoundsChange={handleZoomChange}
            >
              <SearchControl options={{float: 'left'}} />
              <ZoomControl options={{position: {right: 10, top: 100}, size: 'small'}} />
              <GeolocationControl options={{position: {right: 10, top: 180}}} />
              {renderClusterer(clustererId, showClusterer, () =>
                points.map((point) => (
                  <Placemark
                    key={point.id}
                    geometry={point.coordinates}
                    properties={{balloonContent: getBalloonContent(point)}}
                    options={{
                      preset: 'islands#circleDotIcon',
                      iconColor: point.color,
                      hideIconOnBalloonOpen: false,
                    }}
                    instanceRef={(map) => {
                      map?.events?.add('balloonopen', () => {
                        const button = getRedirectButton(point.id);
                        button?.addEventListener('click', goToServiceRequest);
                      });

                      map?.events?.add('balloonclose', () => {
                        const button = getRedirectButton(point.id);
                        button?.removeEventListener('click', goToServiceRequest);
                      });
                    }}
                  />
                )),
              )}
            </Map>
          </YMaps>
        </FallbackWithChildren>
        <MapLegend />
      </Stack>
    </Stack>
  );
};
